/**
 * Chapter006.js — Risk Matrix (OHSE 006)
 * Migrated from SWMS Platform Chapter006.tsx → JavaScript
 *
 * Static reference chapter — risk matrix tables with consequence, likelihood,
 * and risk class/ranking definitions. No user-editable data fields.
 *
 * Props:
 *   companyName {string}  from companyInfo.name
 */

// ── Risk matrix constants (hardcoded from riskCalculator.ts) ──────────────────

const LIKELIHOOD_OPTIONS = [
  { value: 1, label: 'Likely', description: 'Very likely to occur' },
  { value: 2, label: 'Moderate', description: 'Could occur' },
  { value: 3, label: 'Unlikely', description: 'Unlikely to occur' },
];

const CONSEQUENCES_OPTIONS = [
  { value: 1, label: 'High', description: 'Severe injury or death' },
  { value: 2, label: 'Medium', description: 'Medical treatment required' },
  { value: 3, label: 'Low', description: 'Minor or no injury' },
];

// 3×3 matrix: rows=consequence, cols=likelihood
const RISK_MATRIX = [
  // Likely       Moderate     Unlikely
  ['High',       'High',      'Medium'],    // High consequence
  ['High',       'Medium',    'Low'],       // Medium consequence
  ['Medium',     'Low',       'Low'],       // Low consequence
];

const CONSEQUENCE_TABLE = [
  {
    level: 'High (1)',
    subtitle: 'High level of harm',
    description: 'Potential death, permanent disability or major structural failure/damage. Off-site environmental discharge/release not contained and significant long-term environmental harm.',
  },
  {
    level: 'Medium (2)',
    subtitle: 'Medium level of harm',
    description: 'Potential temporary disability or minor structural failure/damage. On-site environmental discharge/release contained, minor remediation required, short-term environmental harm.',
  },
  {
    level: 'Low (3)',
    subtitle: 'Low level of harm',
    description: 'Incident that has the potential to cause persons to require first aid. On-site environmental discharge/release immediately contained, minor level clean up with no short-term environmental harm.',
  },
];

const LIKELIHOOD_TABLE = [
  { level: 'Likely', description: 'Could happen frequently' },
  { level: 'Moderate', description: 'Could happen occasionally' },
  { level: 'Unlikely', description: 'May occur only in exceptional circumstances.' },
];

const RANKING_TABLE = [
  { class: 1, description: 'Will require detailed pre-planning. Actions will be recorded on a Safe Work Method Statement', color: 'bg-red-500' },
  { class: 2, description: 'Will require operational planning. Actions will be recorded on a Safe Work Method Statement', color: 'bg-amber-400' },
  { class: 3, description: 'Will require localised control measures', color: 'bg-green-500' },
];

const CELL_COLORS = { High: 'bg-red-500', Medium: 'bg-amber-400', Low: 'bg-green-500' };

function Co({ cn }) {
  return <span className="font-semibold text-[#c9a84c]">{cn}</span>;
}

export default function Chapter006({ companyName }) {
  const cn = companyName || '[Company Name]';

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest mb-1">OHSE 006</p>
        <h2 className="text-xl font-bold text-[#1a1a2e] uppercase tracking-wide">
          Risk Matrix
        </h2>
      </div>

      <div className="bg-white border border-[#e0dbd0] rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-[#f0ede6]">
          <h3 className="text-sm font-semibold text-[#1a1a2e] uppercase tracking-wide">
            Risk Assessment
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Risk classification and ranking methodology
          </p>
        </div>
        <div className="p-6 space-y-5 text-sm text-[#1a1a2e] leading-relaxed">
          <p>
            <Co cn={cn} /> has identified a risk class/ranking for potential workplace hazards
            by referring to the categories in the matrix below.
          </p>

          {/* Step 1 */}
          <div>
            <p className="font-semibold">
              <span className="text-[#c9a84c]">Step 1:</span>{' '}
              The organisation identifies the consequence for each potential risk by using the
              table below. <span className="italic">Note: If a combination of harm, loss or damage could occur the worst case consequence is selected.</span>
            </p>
          </div>

          {/* Consequence Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="p-3 text-left text-xs font-semibold text-[#6b6560] uppercase border border-[#e0dbd0] bg-[#f0ede6] w-44">Level</th>
                  <th className="p-3 text-left text-xs font-semibold text-[#6b6560] uppercase border border-[#e0dbd0] bg-[#f0ede6]">Description of Consequence</th>
                </tr>
              </thead>
              <tbody>
                {CONSEQUENCE_TABLE.map((row, idx) => (
                  <tr key={idx}>
                    <td className="p-3 border border-[#e0dbd0] bg-[#f8f7f4] align-top">
                      <span className="font-semibold text-[#1a1a2e]">{row.level}</span>
                      <br />
                      <span className="text-[#6b6560] text-xs">({row.subtitle})</span>
                    </td>
                    <td className="p-3 border border-[#e0dbd0] align-top leading-relaxed">{row.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Step 2 */}
          <div>
            <p className="font-semibold">
              <span className="text-[#c9a84c]">Step 2:</span>{' '}
              Using the following table, the organisation determines how likely it is that the
              risk will occur and result in the consequence identified above.
            </p>
          </div>

          {/* Likelihood Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="p-3 text-left text-xs font-semibold text-[#6b6560] uppercase border border-[#e0dbd0] bg-[#f0ede6] w-36">Level</th>
                  <th className="p-3 text-left text-xs font-semibold text-[#6b6560] uppercase border border-[#e0dbd0] bg-[#f0ede6]">Likelihood / Probability</th>
                </tr>
              </thead>
              <tbody>
                {LIKELIHOOD_TABLE.map((row, idx) => (
                  <tr key={idx}>
                    <td className="p-3 font-semibold text-[#1a1a2e] border border-[#e0dbd0] bg-[#f8f7f4]">{row.level}</td>
                    <td className="p-3 border border-[#e0dbd0]">{row.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Step 3 */}
          <div>
            <p className="font-semibold">
              <span className="text-[#c9a84c]">Step 3:</span>{' '}
              Using the risk matrix below, the organisation identifies the risk class/ranking.
            </p>
          </div>

          {/* Risk Matrix Grid */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="p-3 text-left text-xs font-semibold text-[#6b6560] uppercase border border-[#e0dbd0] bg-[#f0ede6]">
                    Consequence ↓ / Likelihood →
                  </th>
                  {LIKELIHOOD_OPTIONS.map(opt => (
                    <th key={opt.value} className="p-3 text-center text-xs font-semibold text-[#6b6560] uppercase border border-[#e0dbd0] bg-[#f0ede6]">
                      {opt.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {RISK_MATRIX.map((row, ri) => (
                  <tr key={ri}>
                    <td className="p-3 font-medium text-[#1a1a2e] border border-[#e0dbd0] bg-[#f8f7f4]">
                      {CONSEQUENCES_OPTIONS[ri].label}
                    </td>
                    {row.map((rating, ci) => (
                      <td key={ci} className={`${CELL_COLORS[rating]} p-3 border text-center font-bold text-sm text-black`}>
                        {rating === 'High' ? 1 : rating === 'Medium' ? 2 : 3}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Risk Ranking Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="p-3 text-left text-xs font-semibold text-[#6b6560] uppercase border border-[#e0dbd0] bg-[#f0ede6] w-36">Class/Ranking</th>
                  <th className="p-3 text-left text-xs font-semibold text-[#6b6560] uppercase border border-[#e0dbd0] bg-[#f0ede6]">Description / Requirements</th>
                </tr>
              </thead>
              <tbody>
                {RANKING_TABLE.map(row => (
                  <tr key={row.class}>
                    <td className={`${row.color} text-black font-bold text-sm p-3 border border-[#e0dbd0] text-center`}>
                      {row.class}
                    </td>
                    <td className="p-3 border border-[#e0dbd0] leading-relaxed whitespace-pre-line">{row.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
